/**
 * ProductsPanel — SKU management: pricing, performance, R&D pipeline,
 * and new-product design studio.
 */

import { useState, type ReactNode } from 'react';
import { toast } from '@/hooks/useToast';
import { Plus, Star, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { PanelSection } from '@/components/game/ui';
import { MattressArt } from '@/components/game/art/MattressArt';
import { useGameStore } from '@/store/gameStore';
import { money, num, pct, formatGameDate } from '@/game/format';
import { ARCHETYPES, MAX_PRODUCTS } from '@/game/constants';
import { returnRateFor } from '@/game/economy';
import { archetype, factoryCostForQuality } from '@/game/products';
import { has } from '@/game/research';
import type { ArchetypeId } from '@/game/types';

export function ProductsPanel() {
  const state = useGameStore((s) => s.state)!;
  const setPrice = useGameStore((s) => s.setPrice);
  const designProduct = useGameStore((s) => s.designProduct);
  const [designOpen, setDesignOpen] = useState(false);

  const live = state.products.filter((p) => p.status !== 'discontinued');
  const canDesign = live.length < MAX_PRODUCTS;

  return (
    <div className="space-y-6">
      <PanelSection
        title={`Product Line (${live.length}/${MAX_PRODUCTS})`}
        desc="Price changes apply instantly across channels. Watch your margin vs. landed cost."
        right={
          <Button size="sm" disabled={!canDesign} onClick={() => setDesignOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> New product
          </Button>
        }
      >
        <div className="space-y-3">
          {state.products.map((p) => (
            <Card key={p.id} className={cn('overflow-hidden', p.status === 'discontinued' && 'opacity-50')}>
              <CardContent className="space-y-3 py-4">
                <div className="flex items-start gap-3">
                  <div className={cn('w-24 shrink-0 sm:w-28', p.status === 'development' && 'opacity-45')}>
                    <MattressArt archetype={p.archetype} quality={p.quality} coolingScore={p.coolingScore} seed={p.id} className="w-full" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-semibold">{p.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {archetype(p.archetype).name} · Quality {Math.round(p.quality)} · Cooling {Math.round(p.coolingScore)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {p.status === 'development' ? (
                          <Badge variant="secondary">R&D — ready {p.devCompleteDay ? formatGameDate(p.devCompleteDay) : ''}</Badge>
                        ) : p.status === 'active' ? (
                          <Badge variant="outline" className="border-emerald-600/40 text-emerald-600">Live</Badge>
                        ) : (
                          <Badge variant="outline">Discontinued</Badge>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground sm:hidden">
                      <span>Price <strong className="text-foreground tabular-nums">{money(p.price)}</strong></span>
                      <span>Stock <strong className="text-foreground tabular-nums">{num(state.inventory[p.id] ?? 0)}</strong></span>
                    </div>
                  </div>
                </div>

                {p.status === 'active' && (
                  <>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                      <Metric label="Price (queen)" value={money(p.price)} />
                      <Metric label="Landed cost" value={money(p.landedCost)} />
                      <Metric
                        label="Gross margin"
                        value={pct(Math.max(0, 1 - p.landedCost / Math.max(1, p.price * 0.98)), 0)}
                      />
                      <Metric label="Return rate" value={pct(returnRateFor(state, p))} />
                      <Metric
                        label="Reviews"
                        value={<span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />{p.reviews.rating.toFixed(2)} ({num(p.reviews.count)})</span>}
                      />
                      <Metric label="Units sold" value={num(p.unitsSoldTotal)} />
                      <Metric label="In stock" value={num(state.inventory[p.id] ?? 0)} />
                      <Metric label="On order" value={num(state.pos.filter((po) => po.productId === p.id).reduce((a, po) => a + po.units, 0))} />
                    </div>

                    <div className="flex flex-wrap items-end gap-2">
                      <div className="w-36">
                        <Label htmlFor={`price-${p.id}`} className="text-xs text-muted-foreground">Queen price ($)</Label>
                        <Input
                          id={`price-${p.id}`}
                          type="number"
                          defaultValue={p.price}
                          min={149}
                          max={3999}
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (Number.isFinite(v) && v !== p.price) setPrice(p.id, v);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </PanelSection>

      <DesignDialog open={designOpen} onOpenChange={setDesignOpen} onSubmit={(spec) => {
        const err = designProduct(spec);
        if (err) toast({ title: 'Cannot start development', description: err, variant: 'destructive' });
        else { toast({ title: 'R&D underway', description: `${spec.name || 'New SKU'} enters sampling.` }); setDesignOpen(false); }
      }} cash={state.player.cash} day={state.time.day} />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium tabular-nums">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Design studio
// ---------------------------------------------------------------------------

interface DesignSpec {
  name: string;
  archetypeId: ArchetypeId;
  quality: number;
  price: number;
}

function DesignDialog({
  open, onOpenChange, onSubmit, cash, day,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSubmit: (spec: DesignSpec) => void;
  cash: number;
  day: number;
}) {
  const [name, setName] = useState('');
  const [archetypeId, setArchetypeId] = useState<ArchetypeId>('allFoam');
  const [quality, setQuality] = useState(60);
  const [price, setPrice] = useState(649);

  const a = ARCHETYPES.find((x) => x.id === archetypeId)!;
  const stateNow = useGameStore.getState().state!;
  const unlocked = !a.requiresResearch || has(stateNow, a.requiresResearch);
  const factory = factoryCostForQuality(a, quality);
  // Rough landed estimate using China rates as the pessimistic default.
  const estLanded = factory * 1.9 + 30;
  const margin = 1 - estLanded / Math.max(1, price * 0.98);
  const previewCooling = Math.round(Math.min(98, Math.max(10, a.coolingBase + (quality - 60) * 0.4)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Design a new mattress</DialogTitle>
          <DialogDescription>Pick an archetype, spec the quality, set the price. The sketch updates as you tune.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Live design preview */}
          <div className="flex items-center justify-center rounded-xl border bg-gradient-to-b from-primary/5 to-transparent p-2">
            <MattressArt archetype={archetypeId} quality={quality} coolingScore={previewCooling} seed="design-preview" className="w-52" />
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {ARCHETYPES.map((arc) => {
              const arcUnlocked = !arc.requiresResearch || has(useGameStore.getState().state!, arc.requiresResearch);
              return (
                <button
                  key={arc.id}
                  disabled={!arcUnlocked}
                  onClick={() => { setArchetypeId(arc.id); setPrice(Math.round(arc.fairValue)); }}
                  className={cn(
                    'rounded-xl border p-3 text-left transition-colors hover:border-primary/60 disabled:cursor-not-allowed disabled:opacity-40',
                    archetypeId === arc.id && 'border-primary ring-1 ring-primary',
                  )}
                >
                  <p className="text-sm font-medium">{arc.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{arc.blurb}</p>
                  <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                    Fair value ~{money(arc.fairValue)} · Dev {money(arc.devCost)} · {arc.devDays}d
                    {!arcUnlocked && ` · 🔒 ${arc.requiresResearch}`}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sku-name">Product name</Label>
            <Input id="sku-name" placeholder='e.g. "Aurora Hybrid"' value={name} onChange={(e) => setName(e.target.value)} maxLength={42} />
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between"><Label>Quality spec</Label><span className="text-sm tabular-nums">{quality}</span></div>
            <Slider value={[quality]} min={30} max={95} step={1} onValueChange={(v) => setQuality(v[0])} />
            <p className="text-xs text-muted-foreground">Factory cost {money(factory)} · higher quality ⇒ fewer returns, better reviews.</p>
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between"><Label>Queen price</Label><span className="text-sm tabular-nums">{money(price)}</span></div>
            <Slider value={[price]} min={199} max={2999} step={10} onValueChange={(v) => setPrice(v[0])} />
            <p className="text-xs text-muted-foreground">
              Est. landed ≈ {money(estLanded)} (China rates) → gross margin ≈{' '}
              <span className={cn('font-medium', margin > 0.45 ? 'text-emerald-600 dark:text-emerald-400' : margin > 0.25 ? '' : 'text-red-600 dark:text-red-400')}>
                {Number.isFinite(margin) ? pct(margin, 0) : '—'}
              </span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}><X className="mr-1.5 h-4 w-4" /> Cancel</Button>
          <Button
            disabled={!unlocked || cash < a.devCost}
            onClick={() => onSubmit({ name, archetypeId, quality, price })}
          >
            Invest {money(a.devCost)}
          </Button>
        </DialogFooter>
        {cash < a.devCost && <p className="text-center text-xs text-red-500">Need {money(a.devCost)} — you have {money(cash)} (day {day}).</p>}
      </DialogContent>
    </Dialog>
  );
}
